import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Truck, Info } from "lucide-react";

/**
 * ShipDialog — captures tracking number + URL when a seller marks an order shipped.
 * Tracking number is MANDATORY for physical products. For services (no parcel), it is optional.
 */
const ShipDialog = ({ open, onClose, onConfirm, busy, isService = false }) => {
  const [carrier, setCarrier] = useState("");
  const [number, setNumber] = useState("");
  const [url, setUrl] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setCarrier("");
      setNumber("");
      setUrl("");
      setTouched(false);
    }
  }, [open]);

  const trackingMissing = !isService && !number.trim();

  const handle = () => {
    setTouched(true);
    if (trackingMissing) return;
    onConfirm({ carrier: carrier.trim(), tracking_number: number.trim(), tracking_url: url.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ship-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#C05A3C]" /> Mark order as shipped
          </DialogTitle>
          <DialogDescription>
            {isService
              ? "Confirm delivery details for this service order. Tracking is optional for services."
              : "A tracking number is required so the buyer can follow their parcel."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Carrier</label>
            <Input
              data-testid="ship-carrier"
              placeholder="e.g. Royal Mail, DHL, USPS, GIG Logistics"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tracking number {isService ? "(optional)" : <span className="text-[#C05A3C]">*</span>}
            </label>
            <Input
              data-testid="ship-tracking-number"
              placeholder="e.g. LX123456789GB"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              required={!isService}
              aria-invalid={touched && trackingMissing}
              className={`mt-1 ${touched && trackingMissing ? "border-[#C05A3C] focus-visible:ring-[#C05A3C]" : ""}`}
            />
            {touched && trackingMissing && (
              <p data-testid="ship-tracking-error" className="text-xs text-[#C05A3C] mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" /> Tracking number is required for physical goods.
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tracking URL (optional)</label>
            <Input
              data-testid="ship-tracking-url"
              placeholder="https://track.example.com/LX123456789GB"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            data-testid="ship-confirm-btn"
            onClick={handle}
            disabled={busy || trackingMissing}
            className="bg-[#C05A3C] hover:bg-[#A8492F] text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
            Mark as shipped
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShipDialog;
